import React from "react";
import { Gift, PiggyBank, HeartHandshake, CloudSun, Clock3, Lock } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";

const perks = [
  {
    icon: Gift,
    title: "Refinance any time",
    body: "Locked into an old, overpriced policy? You can refi your life insurance just like a mortgage — new rate, no gap in coverage, and often more protection.",
    highlight: true,
  },
  {
    icon: PiggyBank,
    title: "Save up to 30%",
    body: "Members switching to Advanced Life Policies save an average of 22% — and often far more — versus their previous cover.",
  },
  {
    icon: Clock3,
    title: "60-second quote",
    body: "No forms that never end. Answer three quick questions and your estimate appears instantly.",
  },
  {
    icon: HeartHandshake,
    title: "No pressure, ever",
    body: "No pushy calls, no jargon. Just an honest number and a licensed advisor who has your back.",
  },
  {
    icon: CloudSun,
    title: "Clear-sky pricing",
    body: "No hidden fees, no stormy surprises — the rate we quote is the rate you pay, in writing.",
  },
  {
    icon: Lock,
    title: "Rate locked in",
    body: "Reserve today's price and it's yours to keep — no surprise hikes, no fine print.",
  },
];

export default function WhyUs() {
  return (
    <section id="why" className="bg-forest text-cream py-24 lg:py-32 relative overflow-hidden" data-testid="why-section">
      <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-sage/10 blur-3xl" />
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <FadeUp className="lg:col-span-7">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-sage-light">Why sign up with Advanced Life Policies</span>
            <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
              Real rewards for doing<br className="hidden sm:block" /> the responsible thing.
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <p className="text-cream/70 leading-relaxed">
              Life cover shouldn&apos;t feel like a chore. We stacked the deck with reasons to start today —
              because protecting the people you love should feel good, not stressful.
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {perks.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeUp key={p.title} delay={i * 0.07}>
                <div
                  data-testid={`perk-card-${i}`}
                  className={`group h-full rounded-3xl border p-8 transition-all duration-500 hover:-translate-y-1.5 ${
                    p.highlight
                      ? "bg-terracotta border-terracotta text-cream shadow-xl shadow-black/20"
                      : "bg-forest-700/40 border-cream/10 hover:border-cream/25"
                  }`}
                >
                  <span
                    className={`grid place-items-center h-12 w-12 rounded-2xl transition-transform duration-500 group-hover:-rotate-6 ${
                      p.highlight ? "bg-cream/20" : "bg-sage/15 text-sage-light"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl tracking-tight">{p.title}</h3>
                  <p className={`mt-3 leading-relaxed ${p.highlight ? "text-cream/85" : "text-cream/65"}`}>{p.body}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}
